/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import AdobeExperienceCloud from '@exc/brand-icons/ExperienceCloud.svg';
import AdobeExperiencePlatform from '@exc/brand-icons/ExperiencePlatform.svg';
import AdobeExperienceSubCloud from '@exc/brand-icons/ExperienceSubCloud.svg';
import {AuthInstance, getBestSupportedLocale} from '@exc/auth';
import CoreContext from './context';
import {debounceRaF} from '@exc/dom';
import discoveryUtil from './utils/discovery';
import {ErrorView, getCaasFrame, getSettingsService, getShowShellToast, ShellNoAccess} from './imports/ImportManager';
import {getAvatarUrls, getOrgName} from './utils/utils';
import {getOmegaEvent} from '@exc/events';
import {
  getPathWithoutTenant,
  getQueryValue,
  getSandboxFromPath,
  getTenantFromPath,
  hashToPath,
  updatePath,
  updateSandboxInPath
} from '@exc/url';
import {history} from '@exc/router';
import metrics from '@adobe/exc-app/metrics';
import {Internal as NetworkInternal} from '@adobe/exc-app/network';
import PropTypes from 'prop-types';
import React from 'react';
import {SettingsLevel} from '@adobe/exc-app/settings';
import ShellWrapper from './Shell';
import {storage} from '@exc/storage';
import TranslationService from './services/TranslationService';
import unifiedShellSession from './services/UnifiedShellSessionService';
import {withLocalization} from '@adobe/adobe-intl-react';

const NOOP = () => {};

const AVATAR_SIZES = [110, 115];
const TEN_HOURS = 1000 * 60 * 60 * 10;
const TOAST_TIMEOUT = 11000;
const UNIFIED_SHELL_APPID = 'shell';
const SUPPORTED_LOCALES = ['de-DE', 'en-US', 'es-ES', 'fr-FR', 'it-IT', 'ja-JP', 'ko-KR', 'pt-BR', 'zh-CN', 'zh-TW'];
const SANDBOX_TYPE_PRODUCTION = 'production';

const SHELL_SIDE_NAV_WIDTH = 222;
const SHELL_HEIGHT_WITH_SANDBOX = 80;
const SHELL_HEIGHT_WITHOUT_SANDBOX = 48;

export const AUTH_TYPE = {
  TYPE1: 'ind',
  TYPE2E: 'eoa',
  TYPE3: 'fed'
};

export const HERO_CONFIGS = {
  application: {
    icon: AdobeExperienceSubCloud,
    iconName: 'AdobeExperienceSubCloud',
    shortTitle: 'Adobe Experience Cloud'
  },
  cloud: {
    icon: AdobeExperienceCloud,
    iconName: 'AdobeExperienceCloud',
    path: '/home',
    shortTitle: 'Adobe Experience Cloud'
  },
  platform: {
    icon: AdobeExperiencePlatform,
    iconName: 'AdobeExperiencePlatform',
    shortTitle: 'Adobe Experience Platform'
  },
  service: {
    icon: AdobeExperienceCloud,
    iconName: 'AdobeExperienceCloud',
    shortTitle: 'Adobe Experience Cloud'
  }
};

const cleanTenant = tenant => tenant && tenant.split(':')[0];

class Core extends React.Component {
  constructor(props) {
    super(props);

    this.config = props.config;
    this.metrics = metrics.create('exc.core.Core');
    this.shellTimer = props.shellTimer || this.metrics.start('Shell');

    const hero = HERO_CONFIGS.cloud;
    this.favicon = hero.icon;
    this.nonSupportedBrowser = false;

    this.state = {
      accessToken: null,
      auth: null,
      caas: null,
      coachMark: {},
      coachMarkPosition: null,
      customEnvLabel: '',
      customHeroClick: false,
      customSearch: {},
      defaultComponent: this.config.defaultComponent,
      enhancedImsOrgs: [],
      environment: this.config.environment,
      error: null,
      featureFlags: null,
      feedback: {},
      helpCenter: {},
      hero,
      imsOrgs: [],
      locale: 'en-US',
      modal: false,
      nps: {
        enabled: false,
        sampling: 10,
        showToAdobeUsers: true
      },
      npsScore: null,
      onCustomButtonClick: NOOP,
      orgsMap: {},
      orgToSubOrgsMap: {},
      profile: null,
      sandbox: {
        enabled: false,
        onChange: this.onSandboxChange,
        sandboxes: [],
        selected: null
      },
      selectedImsOrg: null,
      selectedSubOrg: null,
      shellResponse: {},
      sidebar: {},
      solutionConfig: {},
      supportsViewportTakeover: false,
      userAccounts: null,
      viewportTakeover: false,
      workspaces: []
    };

    this.getSideNavState();

    const {endpoints} = this.config;
    this.translationService = new TranslationService(endpoints.translation);
    this.hasTenantInUrl = true;
    this.history = props.history || history;
    this.orgTenantMap = {};
    this.debouncedUpdateTargetNode = debounceRaF(this.updateTargetNode);
    this.tenantOrgMap = {};
    this.userServiceCodes = {};
    this.unlisten = this.history.listen(this.onHistoryChange);
    this.settingsHandler = this.settingsHandler.bind(this);
    this.settingsRef = {
      SETTINGS_LEVEL: SettingsLevel,
      settingsHandler: this.settingsHandler
    };

    // Tokens are handled in sessionstorage. If there are lingering local storage cached tokens,
    // remove them completely.
    storage.local.remove('shellCachedTokens');
  }

  async componentDidMount() {
    const auth = await AuthInstance();

    this.postAuth = import('./postAuth');
    // Check if the current browser is supported and show a message if it's not.
    this.postAuth.then(({supportedBrowsers, getIframeEntry, getPerformanceMarks}) => {
      this.getPerformanceMarks = getPerformanceMarks;
      this.getIframeEntry = getIframeEntry;
      if (!supportedBrowsers.test(window.navigator.userAgent)) {
        this.nonSupportedBrowser = true;
        this.metrics.warn('This browser is not supported.');
        this.showToast('browserlistWarning', {variant: 'info'});
      }

      this.logoutBroadcaster = new BroadcastChannel('logout');
      this.logoutReciever = new BroadcastChannel('logout');
      this.logoutReciever.addEventListener('message', () => {
        if (this.logoutSender) {
          this.logoutSender = false;
        } else {
          this.silentSignOut(true);
        }
      });
    });

    this.setState({auth}, () => this.authProcessing());
    window.addEventListener('resize', this.debouncedUpdateTargetNode);
  }

  componentWillUnmount() {
    this.unlisten && this.unlisten();
    window.removeEventListener('resize', this.debouncedUpdateTargetNode);
  }

  componentDidUpdate() {
    if (this.props.error && !this.state.error) {
      this.setState({error: this.props.error});
    }
  }

  async getSideNavState() {
    let sideNavOpen = await storage.session.get('sideNavOpen');
    if (sideNavOpen === null) {
      sideNavOpen = true;
    }
    this.setState({sideNavOpen});
    return sideNavOpen;
  }

  /**
   * Gets the Account Cluster data from GraphQL
   * @param {Promise<GraphQL Response>} userDataPromise Behance data
   * @param imsOrgs IMS orgs array
   * @param {IMSProfile} profile Current profile
   */
  async getAdditionalUserData(userDataPromise, imsOrgs, profile, locale) {
    const {getAccountClusterData} = await this.postAuth;
    const {imsAccountClusterData: accountCluster} = await userDataPromise.catch(() => {}) || {};
    const {enhancedImsOrgs, userAccounts} = getAccountClusterData(accountCluster, imsOrgs, locale, profile, this.props.formatMessage);

    if (this.state.showNoAccessShell && userAccounts) {
      this.metrics.info(`selectedImsOrg undefined in Core.js authProcessing, with ${userAccounts.length - 1} other account(s)`);
    }

    this.setState({enhancedImsOrgs, hasAccountClusterData: true, userAccounts});
  }

  /**
   * Gets the following data from GraphQL:
   *   1. Pulse (Timeline, Org Settings & Preferences) - To update the Shell header.
   *   2. Next profile - Only if we are using a cached profile (Reload), used to ensure a new open tab will always contain a fresh profile.
   *   3. Avatar (Via the User Data promise) - To update in both current and next profile with avatar, in case any SPA uses the avatar (i.e. Preferences)
   * @param {string} locale Current locale to get localized notifications.
   * @param {Promise<GraphQL Response>} userDataPromise Behance data
   * @param {IMSProfile} profile Current profile
   */
  async getHeaderData(locale, userDataPromise, profile) {
    const {auth} = this.state;
    const pulseDataPromise = auth.gqlHandlerForHeader(locale, !this.props.isLogin);
    const {userBehance} = await userDataPromise.catch(() => {}) || {};
    const {avatar, avatarSrc: userAvatar} = getAvatarUrls(userBehance, AVATAR_SIZES);

    if (userAvatar && profile.avatarSrc !== userAvatar) {
      // Different or no avatar in profile. Update state.
      profile.avatar = avatar;
      profile.avatarSrc = userAvatar;
      this.setState({profile, userAvatar});
    }

    const {
      timeline: timelineResponse,
      userOrgPreferences,
      userOrgSettings: settingsResponse,
      userProfileJson} = await pulseDataPromise || {};

    this.setState({settingsResponse, timelineResponse, userOrgPreferences});

    // In case we used a stored profile, we got a fresh profile and store it in the session.
    // This ensures on browser refresh profile will be fresh.
    if (userProfileJson) {
      if (userAvatar) {
        // Add the avatar to the profile we are caching.
        // If the user refresh the browser, the avatar will show right away.
        userProfileJson.avatar = avatar;
        userProfileJson.avatarSrc = userAvatar;
      }
      auth.updateProfile(userProfileJson);
    }
  }

  async getLaunchDarklyData(setState = true, selectedImsOrg = this.state.selectedImsOrg) {
    const ldDataTimer = this.metrics.start('LaunchDarklyData');
    const {auth} = this.state;
    let featureFlags = await unifiedShellSession.get('featureFlags', selectedImsOrg);

    try {
      // This promise is used to post-fetch the launch darkly data from GQL in
      // the event that it already exists in local storage so that it will
      // always be up-to-date on refreshes without needing to log out to get
      // updated data. In the event that it's not in local storage, this promise
      // is awaited on so that it's available immediately.
      const featureFlagPromise = new Promise((resolve, reject) => {
        auth.gqlOrgLaunchDarkly().then(async ({imsOrgFeatureFlagSettings: {featureFlags}}) => {
          const flags = {};
          featureFlags.forEach(({name, value}) => (flags[name] = value));
          // Update unifiedShellSession with the flags for the current ims org so
          // that next time it can pull from storage.
          unifiedShellSession.update('featureFlags', flags, selectedImsOrg, true);
          resolve(flags);
        }).catch(() => reject());
      });
      featureFlags = featureFlags || await featureFlagPromise;
    } catch (e) {
      this.metrics.error('Failed to retrieve feature flags from launch darkly via GQL');
      featureFlags = {};
    }

    this.appAssemblyService.setFeatureFlags(featureFlags);
    const shellResponse = this.getShellConfiguration(selectedImsOrg);
    const state = {featureFlags, shellResponse};
    setState && this.setState(state);
    ldDataTimer.time('done', 'feature flags were pulled from GQL');
    return state;
  }

  configureNetwork(selectedImsOrg) {
    const {auth} = this.state;
    const imsClientId = auth.getClientId();
    //configure network runtime API
    NetworkInternal.configure({
      apiGatewayUrl: this.config.endpoints.apiGateway,
      appId: UNIFIED_SHELL_APPID,
      getTokenInfo: async () => ({token: auth.fetchAccessToken()}),
      imsClientId,
      imsOrg: selectedImsOrg || this.state.selectedImsOrg
    });
  }

  /**
   * Get user orgs from GraphQL with IMS fallback, and store to session.
   * @param {object} gqlResponse - GraphQL call response
   * @returns {Promise<userOrgs>} User Orgs
   */
  async getUserOrgs(gqlResponse) {
    const {auth} = this.state;
    let {userOrgs} = gqlResponse || {};
    userOrgs = userOrgs || await auth.fetchOrgs().catch(error => {
      this.metrics.error('Failed to get orgs data from both GraphQL and IMS.', {error});
    });
    userOrgs && unifiedShellSession.update('userOrgs', userOrgs);
    return userOrgs;
  }

  async authProcessing() {
    const authProcessingTimer = this.metrics.start('AuthProcessing');

    const {auth} = this.state;
    // Grab the org from the path and use that as the selected org. If there
    // is no org yet, use the selected one from IMS.
    let pathTenant = getTenantFromPath(this.history.location.pathname, true);

    // Use cached avatar from session profile (if we have it) until we get a fresh avatar from GraphQL.
    const {avatarSrc: userAvatar} = auth.fetchUserProfile(false) || {};
    userAvatar && this.setState({userAvatar});

    this.configureNetwork();

    // Call GraphQL to fetch profile, Org and IMS session data.
    const gqlHandlerPromise = auth.gqlHandler(!this.props.isLogin, pathTenant);
    const userOrgsPromise = gqlHandlerPromise.then(response => this.getUserOrgs(response));

    // Also call GraphQL to fetch IMS account cluster data.
    const userDataPromise = auth.getAdditionalUserData(AVATAR_SIZES);
    let orgsMap = [];

    if (this.props.isLogin) {
      // Wait for GraphQL if this is a login.
      orgsMap = await userOrgsPromise;
    } else {
      // Use cache if this is a reload.
      const cachedOrgs = await unifiedShellSession.get('userOrgs');
      orgsMap = auth.allOrgsInProfile(cachedOrgs) ? cachedOrgs : await userOrgsPromise;
    }

    let profile = auth.fetchUserProfile();
    let accessToken = auth.fetchAccessToken();

    const locale = getBestSupportedLocale(profile, SUPPORTED_LOCALES);
    const productContext = profile.projectedProductContext;

    document.documentElement.setAttribute('lang', locale);

    const allOrgs = new Set;
    let dmaTartanNoTenant = 0;
    const duplicateTenants = {};
    // Grab the tenant ids from the product contexts for all orgs this user
    // is a part of. This will be used to map org id values to tenants for
    // the @value within the path.
    productContext.forEach(ctx => {
      const {owningEntity, serviceCode, tenant_id} = ctx.prodCtx;

      // Keep track of all of the org ids that are in this user's product context.
      allOrgs.add(owningEntity);

      if (serviceCode === 'dma_tartan') {
        tenant_id || dmaTartanNoTenant++;
        this.orgTenantMap[owningEntity] = tenant_id;
        // The tenant already exists in the map. This means that there are
        // duplicate tenants in the product context. Track the orgs so that
        // they can be handled later.
        if (this.tenantOrgMap[tenant_id] && this.tenantOrgMap[tenant_id] !== owningEntity) {
          duplicateTenants[tenant_id] = duplicateTenants[tenant_id] || new Set([this.tenantOrgMap[tenant_id]]);
          duplicateTenants[tenant_id].add(owningEntity);
          this.metrics.error(`Duplicate tenant ID: IMS orgs ${owningEntity} and ${this.tenantOrgMap[tenant_id]} both contain dma_tartan and linked to tenant ${tenant_id}`);
        }
        this.tenantOrgMap[tenant_id] = owningEntity;
      }
      // create map of org id to list of service codes
      if (this.userServiceCodes[owningEntity]) {
        this.userServiceCodes[owningEntity].push(serviceCode);
      } else {
        this.userServiceCodes[owningEntity] = [serviceCode];
      }
    });

    // Track the number of orgs that a user has with the dma_tartan service
    // code and the number without. This will help us determine the need for
    // this service code in the future.
    const numWithTartan = Object.keys(this.orgTenantMap).length;
    this.metrics.info('User dma_tartan orgs information', {
      noTenant: dmaTartanNoTenant,
      with: numWithTartan,
      without: allOrgs.size - numWithTartan
    });

    // Clean up the maps for duplicate tenants
    Object.keys(duplicateTenants).forEach(tenant => {
      const orgsWithDupeTenant = Array.from(duplicateTenants[tenant]).sort();
      // First sorted tenant is the plain tenant
      this.tenantOrgMap[tenant] = orgsWithDupeTenant[0];
      this.orgTenantMap[orgsWithDupeTenant[0]] = tenant;
      // Loop over the rest of the orgs and update the tenant and org maps with
      // a modified tenant key with the <tenant>:<1st 4 characters of org>.
      orgsWithDupeTenant.slice(1).forEach(org => {
        const tenantKey = `${tenant}:${org.slice(0, 4)}`;
        this.tenantOrgMap[tenantKey] = org;
        this.orgTenantMap[org] = tenantKey;
      });
    });

    const fallbackOrgId = this.getFallbackOrg(Object.keys(this.orgTenantMap));
    const environment = this.config.serviceEnvironment;
    let pathOrg = pathTenant;
    if (pathTenant && pathTenant.indexOf('AdobeOrg') === -1) {
      pathOrg = this.tenantOrgMap[pathTenant];
      pathOrg || this.metrics.error(`User doesn't have access to ${pathTenant} tenant`);
    }
    const tenantInPath = !!pathOrg;
    const orgFromIms = this.props.org;

    // Temporary - Will be removed in EXC-15570 to make reload faster.
    // Currently needed to ensure IMS session data is ready.
    await gqlHandlerPromise;

    let [currentImsOrg, session] = await Promise.all([
      orgFromIms || auth.fetchCurrentOrg(),
      auth.getSession()
    ]);

    // Adding `dma_tartan` filtering for the orgsMap here in case it's not
    // filtered properly in GraphQL. `orgTenantMap` only has orgs that have the
    // `dma_tartan` service code.
    orgsMap = orgsMap.filter(({orgRef}) => this.orgTenantMap[`${orgRef.ident}@${orgRef.authSrc}`]);

    let selectedImsOrg = pathOrg || orgFromIms || currentImsOrg || fallbackOrgId;
    // Ensure selected org is present in user's orgs list otherwise use fallback
    let orgIncludedInList = orgsMap.find(org => `${org.orgRef.ident}@AdobeOrg` === selectedImsOrg);
    if (!orgIncludedInList && selectedImsOrg) {
      this.metrics.error(`selectedImsOrg ${selectedImsOrg} not included in orgsMap in Core.js authProcessing`);
      selectedImsOrg = fallbackOrgId;
    }

    const tenantId = this.orgTenantMap[selectedImsOrg] || selectedImsOrg;
    this.metrics.event('Auth.done', {
      defaultOrg: orgFromIms || '',
      defaultTenant: this.orgTenantMap[orgFromIms || ''] || '',
      selectedOrg: selectedImsOrg,
      selectedTenant: cleanTenant(tenantId)
    });

    let {appId, serviceCode} = this.state.solutionConfig;
    let localSessionSubOrgs = await unifiedShellSession.get('activeSubOrgs');
    let currentActiveProductContext;

    if (localSessionSubOrgs && localSessionSubOrgs[appId]) {
      currentActiveProductContext = {
        [serviceCode]: {
          ident: localSessionSubOrgs[appId].id,
          serviceCode
        }
      };
    }
    currentActiveProductContext = currentActiveProductContext || session.current_active_pc || {};
    const activeProductContext = this.getActiveProductContext(currentActiveProductContext, productContext, selectedImsOrg);

    const imsOrgs = orgsMap.map(org => ({
      label: org.orgName,
      value: `${org.orgRef.ident}@${org.orgRef.authSrc}`
    }));

    imsOrgs.sort((a, b) => a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);

    const wasTenantSelected = pathOrg === currentImsOrg;

    this.getAdditionalUserData(userDataPromise, imsOrgs, profile, locale);

    if (!selectedImsOrg) {
      this.setState({locale, profile, showNoAccessShell: true});
      return;
    }
    const selectedImsOrgName = getOrgName(selectedImsOrg, orgsMap);
    this.internal = profile.email && profile.email.indexOf('@adobe') > 0;
    // Falls back to the org id if there is no tenant available for the org
    window.adobeMetrics.setUser({
      accountType: profile.account_type,
      authSystem: 'ims',
      data: {internal: this.internal, tenantId: cleanTenant(tenantId)},
      displayName: profile.displayName,
      groupId: selectedImsOrg,
      groupName: selectedImsOrgName,
      language: locale,
      sessionId: profile.session,
      userId: profile.userId
    });

    const {AppAssemblyService} = await this.postAuth;

    // Instantiate the App Assembly Service when we have the values we need
    this.appAssemblyService = new AppAssemblyService({
      cdn: this.props.config.cdn,
      environment,
      imsOrgs,
      listedApps: this.props.listedApps,
      locale,
      orgTenantMap: this.orgTenantMap,
      profile,
      userRoles: profile.roles,
      userServiceCodes: this.userServiceCodes
    });

    // Redirect to profile page if profile is in path
    if (getPathWithoutTenant(hashToPath()) === '/profile') {
      return window.location.assign(this.appAssemblyService.getProfileComponent(tenantId).editProfileLink);
    }

    this.setState(
      {
        accessToken,
        activeProductContext,
        imsOrgs,
        locale,
        orgsMap,
        profile,
        selectedImsOrg,
        selectedImsOrgName,
        session
      },
      async () => {
        this.configureNetwork();

        this.getHeaderData(locale, userDataPromise, profile);
        this.state.sandbox.enabled && this.getSandboxes();

        const {solutionConfig} = this.state;
        // Only await on the feature flag data request when there is a feature
        // flag required to determine access of the current application. Doing
        // it all the time will cause unnecessary delays to other apps.
        solutionConfig.accessFlag ?
          await this.getLaunchDarklyData() :
          this.getLaunchDarklyData();

        // The tenant is in the URL and it was not the current IMS org in the session.
        if (tenantInPath && !wasTenantSelected) {
          // If the tenant is already in the URL, we don't need to update the path.
          // Instead, trigger an org change to let IMS know that this is the current org
          await this.onImsOrgChange(selectedImsOrg, currentImsOrg, true, null, true);
        } else {
          // The tenant is not in the URL or it is and was already the current IMS
          // org in the session. Get shell configuration and update the URL.
          let shellResponse = this.getShellConfiguration(selectedImsOrg);
          const hero = this.getHeroConfig(shellResponse, solutionConfig, this.state.hero);
          this.setState({favicon: hero.icon, hero, shellResponse});
          // If the org isn't authorized for the solution, redirect to home
          if (!this.isOrgPermissionedOnSolution(selectedImsOrg)) {
            updatePath({path: '/home', tenant: tenantId}, true, this.hasTenantInUrl, false);
            this.metrics.event('Shell.notPermissioned', {appId: solutionConfig.appId, selectedImsOrg});
            await this.showToast('orgHomeRedirectToast', {timeout: TOAST_TIMEOUT, variant: 'info'});
          } else {
            updatePath({tenant: tenantId}, true, this.hasTenantInUrl);
          }
        }

        authProcessingTimer.time('done');
        this.shellTimer.time(this.props.isLogin ? 'isLoginFlow.done' : 'done', {
          gqlEnabled: true,
          region: this.config.region
        }); // step 9 ready
      }
    );
  }

  /**
   * Get a fallback org in a standard way. Currently it sorts the list of org
   * ids in ascending order and takes the first one.
   * @param {string[]} orgs List of org ids.
   * @returns {string} The fallback org.
   */
  getFallbackOrg = orgs => orgs.sort()[0]

  getActiveProductContext = (currentActiveProductContext, productContext, imsOrg) => {
    let activeProductContext = {};
    Object.keys(currentActiveProductContext).map(key => {
      let pc = productContext.find(ctx => ctx.prodCtx.ident === currentActiveProductContext[key].ident) || {};
      // If pc is not valid, delete from local storage and clear state value
      if (!pc.prodCtx || pc.prodCtx.owningEntity !== imsOrg) {
        return activeProductContext;
      }
      activeProductContext[key] = pc.prodCtx;
    });
    return activeProductContext;
  }

  toggleSideNav = (e, auto) => {
    const open = !this.state.sideNavOpen;
    this.setState({sideNavOpen: open});
    storage.session.set('sideNavOpen', open);
    this.metrics.analytics.trackEvent(getOmegaEvent({
      action: auto ? 'SideNavItem click' : 'hamburger click',
      attributes: {opened: open},
      element: e && e.target && e.target.innerText,
      event: e,
      type: 'button',
      widget: {name: 'side nav hamburger', type: 'button'}
    }));
  }

  getComponentContext() {
    const {
      activeProductContext,
      accessToken,
      appContainer,
      coachMark,
      coachMarkPosition,
      customSearch,
      environment,
      featureFlags,
      imsOrgs,
      locale,
      npsScore,
      profile,
      sandbox,
      selectedImsOrg,
      selectedImsOrgName,
      selectedSubOrg,
      session,
      shellResponse
    } = this.state;

    return {
      activeProductContext,
      adobeIMS: window.adobeIMS,
      appContainer,
      avatar: profile && profile.avatar || '',
      cdn: this.props.config.cdn,
      coachMark,
      coachMarkPosition,
      customSearch,
      environment,
      favicon: this.favicon,
      featureFlags,
      formatMessage: this.props.formatMessage,
      getIframeEntry: this.getIframeEntry,
      getPerformanceMarks: this.getPerformanceMarks,
      getToken: customIms => this.getToken(customIms),
      imsClientId: this.config.ims.client_id,
      imsEnvironment: this.config.ims.environment,
      imsOrg: selectedImsOrg,
      imsOrgName: selectedImsOrgName,
      imsOrgs: imsOrgs.filter(({value}) => /@AdobeOrg$/.test(value)),
      imsProfile: profile,
      imsToken: accessToken,
      internal: this.internal,
      loadCaas: this.loadCaas.bind(this),
      locale,
      metricsEnv: this.props.metricsEnv,
      nonSupportedBrowser: this.nonSupportedBrowser,
      npsScore,
      onSignOut: this.onSignOut,
      preferredLanguages: profile && profile.preferred_languages || [],
      resetConfigStates: (appId, nextAppId, nextSideNav) => this.resetConfigStates(appId, nextAppId, nextSideNav),
      sandbox: sandbox.selected,
      sandboxes: sandbox.sandboxes,
      session,
      setConfigStates: config => this.setConfigStates(config),
      setLogoutUrl: url => this.state.auth.logout.add(url),
      setSolutionConfig: this.setSolutionConfig,
      setSubOrgs: this.setSubOrgs,
      settingsService: this.settingsRef,
      shellHeight: sandbox && sandbox.sandboxes && sandbox.sandboxes.length ? SHELL_HEIGHT_WITH_SANDBOX : SHELL_HEIGHT_WITHOUT_SANDBOX,
      shellInfo: shellResponse,
      shellSideNavPresent: this.state.sideNavEnabled && this.state.sidebar.visible !== false && this.state.sideNavOpen,
      shellSideNavWidth: SHELL_SIDE_NAV_WIDTH,
      solutionType: this.config.type,
      subOrg: selectedSubOrg,
      tenant: cleanTenant((this.orgTenantMap || {})[selectedImsOrg] || ''),
      toggleSideNav: this.toggleSideNav,
      translationService: this.translationService
    };
  }

  setConfigStates(config) {
    let {coachMark, customSearch, helpCenter, hero, nps, sidebar} = config;
    if (hero) {
      config.hero = this.getHeroConfig(this.state.shellResponse, this.state.solutionConfig, hero);
    }

    helpCenter && Object.assign(config, {helpCenter: {...this.state.helpCenter, ...helpCenter}});
    nps && Object.assign(config, {nps: this.getNps(nps)});
    customSearch && Object.assign(config, {
      customSearch: {
        // If an enabled value is not explicitly set, maintain the current state
        enabled: (customSearch || {}).enabled === undefined ? this.state.customSearch.enabled : customSearch.enabled,
        onClick: this.onCustomSearchClick,
        open: (customSearch || {}).open || false
      }
    });
    sidebar && Object.assign(config, {
      sidebar: {
        config: !sidebar.config ? this.state.sidebar.config : sidebar.config,
        visible: (sidebar || {}).visible !== undefined ? sidebar.visible : null
      }
    });
    this.setState(config);

    coachMark && this.debouncedUpdateTargetNode();
    nps && this.metrics.event('Shell.configuredNps', {isEnabled: nps.enabled});
  }

  resetConfigStates = (appId, nextAppId, nextSideNav) => {
    // Same app is being loaded next. Don't reset so experience doesn't shift.
    if (appId === nextAppId) {
      return;
    }

    // only reset if there's not another sidenav config
    if (!nextSideNav || nextSideNav === {}) {
      this.setState({sidebar: {}});
    }

    this.setState({
      appContainer: null,
      coachMark: {},
      coachMarkPosition: null,
      customButtons: [],
      customEnvLabel: '',
      customHeroClick: null,
      customSearch: {enabled: false, open: false},
      digitalData: null,
      feedback: {},
      helpCenter: null,
      onCustomButtonClick: NOOP,
      orgToSubOrgsMap: {},
      supportsViewportTakeover: false,
      viewportTakeover: false,
      workspaces: []
    });
  }

  getShellConfiguration(selectedImsOrg) {
    let selectedOrg = selectedImsOrg || this.state.selectedImsOrg;
    return this.appAssemblyService.fetchShell(
      selectedOrg,
      this.orgTenantMap[selectedOrg]
    );
  }

  onImsOrgChange = async (selectedImsOrg, currentImsOrg, replace, selectedSubOrg, init) => {
    const {auth, orgsMap, sandbox} = this.state;

    if (selectedImsOrg === currentImsOrg) {
      this.storeSubOrg(selectedSubOrg);
      return this.setState({selectedSubOrg});
    }

    try {
      auth.changeOrg(selectedImsOrg, currentImsOrg);
      const selectedImsOrgName = getOrgName(selectedImsOrg, orgsMap);
      window.adobeMetrics.setUser({
        data: {internal: this.internal, tenantId: cleanTenant(this.orgTenantMap[selectedImsOrg])},
        groupId: selectedImsOrg,
        groupName: selectedImsOrgName
      });

      const state = {
        selectedImsOrg,
        selectedImsOrgName,
        selectedSubOrg
      };
      this.configureNetwork(selectedImsOrg);
      // Only fetch launch darkly on non-init (authorize) workflows because
      // authorize does it already and onImsOrgChange only happens when the
      // org needs to be changed due to the path's tenant.
      if (!init) {
        const ldData = await this.getLaunchDarklyData(false, selectedImsOrg, selectedImsOrgName);
        Object.assign(state, ldData);
      }

      this.setState(state, () => {
        if (!this.isOrgPermissionedOnSolution(selectedImsOrg)) {
          updatePath({path: '/home'}, replace, this.hasTenantInUrl, false);
          this.showToast('orgSwitchRedirectToast', {timeout: TOAST_TIMEOUT, variant: 'info'});
          this.metrics.event('Shell.notPermissioned', {appId: this.state.solutionConfig.appId, currentImsOrg, selectedImsOrg});
        }
      });
      this.storeSubOrg(selectedSubOrg, selectedImsOrg);
    } catch (error) {
      this.metrics.error(error.message);
      await this.showToast('orgFailToast', {variant: 'error'});
      return;
    }

    // On org change, re-fetch sandboxes.
    sandbox.enabled && this.getSandboxes();

    // Clear Active Product Context
    if (!selectedSubOrg) {
      this.setState({activeProductContext: {}});
    }

    this.metrics.analytics.trackPage();

    // if org changed and no sub org selected, reset selectedSubOrg
    if (selectedImsOrg !== currentImsOrg && !selectedSubOrg) {
      this.setState({selectedSubOrg: {id: null, name: null}});
    }
    return true;
  }

  /**
   * Checks whether an ims org is permissioned on a given solution.
   * @param {string} orgId The IMS org.
   * @param {Object} solutionConfig Optional solution config.
   * @returns {boolean} If the org is permissioned.
   */
  isOrgPermissionedOnSolution(orgId, solutionConfig) {
    const {featureFlags, solutionConfig: config} = this.state;
    const {access = [], accessFlag, accessType = 'OR'} = (solutionConfig || config);
    // There are no access service codes and no access feature flag, so we can
    // assume that this org is permissioned correctly.
    if ((!access || !access.length) && !accessFlag) {
      return true;
    }

    // Determine if the user has the required feature flag. This is a string
    // because it comes from GQL and they are all strings.
    const hasFlag = !accessFlag || featureFlags && featureFlags[accessFlag] === 'true';

    // If there are no service codes set and the flag is true, success.
    if ((!access || !access.length) && hasFlag) {
      return true;
    }

    // Determine if the user has any of the required access service codes.
    const userServiceCodes = this.userServiceCodes[orgId] || [];
    const hasCodes = accessType === 'OR' ?
      access.some(code => userServiceCodes.indexOf(code) > -1) :
      access.every(code => userServiceCodes.indexOf(code) > -1);

    return hasCodes && hasFlag;
  }

  /**
   * Stores sub org in local storage by solution appId
   */
  storeSubOrg = async (selectedSubOrg, selectedImsOrg = this.state.selectedImsOrg) => {
    const {auth, solutionConfig} = this.state;
    const solution = this.state.solutionConfig.appId;
    // This remains unchanged because the structure of it will be changing shortly.
    const localSession = await unifiedShellSession.get();
    localSession.activeSubOrgs = {...localSession.activeSubOrgs, [solution]: selectedSubOrg};
    unifiedShellSession.set(localSession);

    // Update the activeProductContext state
    let profile = auth.fetchUserProfile();
    const productContext = profile.projectedProductContext;

    let {appId, serviceCode} = solutionConfig;
    let currentActiveProductContext = {};
    if (localSession && localSession.activeSubOrgs && localSession.activeSubOrgs[appId]) {
      currentActiveProductContext = {
        [serviceCode]: {
          ident: localSession.activeSubOrgs[appId].id,
          serviceCode
        }
      };
    }
    const activeProductContext = this.getActiveProductContext(currentActiveProductContext, productContext, selectedImsOrg);
    this.setState({activeProductContext});
  }

  /**
   * Displays a toast with a custom message.
   * @param {string} messageId The message ID of the localized string to populate the toast.
   * @param {Object} options Optional object of objects such as variant.
   */
  async showToast(messageId, options) {
    const showShellToast = await getShowShellToast();
    showShellToast(messageId, this.state.locale, options);
  }

  updateUrlOnOrgChange = (selectedImsOrg, currentImsOrg, replace, selectedSubOrg) => {
    // Store the suborg so it is available in onHistoryChange
    this.updatedSubOrg = selectedSubOrg;
    // Since the org has not changed, the subOrg must have changed
    if (selectedImsOrg === currentImsOrg) {
      this.storeSubOrg(selectedSubOrg);
      return this.setState({selectedSubOrg});
    }

    // If the `selectedImsOrg` ends in AdobeID, that means it's an attempt to
    // load an account instead of an org. This will only happen in the following
    // scenarios:
    //  - In Type1 and click on Type2E org in switcher
    //  - In Type2E and click on Type2E org in switcher
    //  - In Type2E and click on Type1 account in switcher
    if (!(/@AdobeOrg$/.test(selectedImsOrg))) {
      const {hash, origin} = window.location;
      const oldHash = getPathWithoutTenant(hash.replace(/^#/, ''));
      const redirect = `${origin}/#from_ims=true&old_hash=${encodeURIComponent(`#${oldHash}`)}`;
      return this.state.auth.authorize(redirect, selectedImsOrg);
    }

    updatePath({tenant: this.orgTenantMap[selectedImsOrg] || selectedImsOrg}, replace, this.hasTenantInUrl);
    // An org switch for a tenantless solution won't change the URL, so no history update is fired.
    // We can get around this by manually switching the org
    if (this.state.solutionConfig.hideTenant) {
      this.onImsOrgChange(selectedImsOrg, currentImsOrg, true, selectedSubOrg);
    }
  }

  onHistoryChange = location => {
    let pathTenant = getTenantFromPath(location.pathname, true);
    let pathOrg = this.tenantOrgMap[pathTenant];
    const {sandbox, selectedImsOrg} = this.state;

    // If sandboxes are enabled and the link does not have it, add it.
    if (sandbox.enabled && sandbox.selected && !getSandboxFromPath(location.pathname)) {
      updateSandboxInPath(sandbox.selected.name, this.hasTenantInUrl);
    }

    // If the org is not set, update the URL with the current one.
    if (!pathOrg && selectedImsOrg) {
      setTimeout(() => updatePath({tenant: this.orgTenantMap[selectedImsOrg] || selectedImsOrg}, true, this.hasTenantInUrl), 0);
    }
    // Compare the new org with the current one. If they are different,
    // trigger an org change. If there is no pathOrg, that means
    // solution has hideTenant and this can be skipped when tenant id isn't
    // the part of the history that changed.
    if (pathOrg && pathOrg !== selectedImsOrg) {
      // Replace argument is `true` because the URL has already been updated,
      // so we don't want to push anything else to the history.
      return this.onImsOrgChange(pathOrg, selectedImsOrg, true, this.updatedSubOrg);
    }
  }

  silentSignOut = async (authExpired, message = 'User signed out') => {
    // Clear Discovery Url Cache
    discoveryUtil.clear();
    storage.session.remove('prevSrc');

    this.metrics.event('Shell.signOut', undefined, message);
    window.adobeMetrics.clearUser();
    await this.state.auth.signOut(authExpired);
  }

  onSignOut = async (authExpired, message = 'User signed out') => {
    this.silentSignOut(authExpired, message);
    this.logoutBroadcaster.postMessage('logout');
  }

  onCustomSearchClick = () => {
    const {enabled, onClick, open} = this.state.customSearch;
    open && this.metrics.analytics.trackEvent({
      action: 'click',
      element: 'custom search',
      feature: 'shell header',
      type: 'button',
      widget: {name: 'custom search', type: 'button'}
    });
    this.setState({customSearch: {enabled, onClick, open: !open}});
  }

  getHeroConfig = (shellResponse, solutionConfig, stateHero) => {
    // Grab the current solution or service in order to get the proper name string
    const allSolutionsAndServices = (shellResponse.solutions || []).concat(shellResponse.services || []);
    const current = allSolutionsAndServices.find(solServ => {
      const apps = Array.isArray(solServ.appId) ? solServ.appId : [solServ.appId];
      return apps.indexOf(solutionConfig.appId) > -1;
    });

    let newHero = HERO_CONFIGS.cloud;
    const type = (current && current.adobeBrandType) || 'cloud';
    if (type && type !== 'cloud') {
      let {longName, name} = current || {};

      // State hero is used here to push runtime changes forward in case getHeroConfig
      // is called in such a way that App Assembly load or a Solution config change occurs
      // after a meaningful runtime change has been made.
      let runtimeOverrides = {};
      if (stateHero) {
        runtimeOverrides = {path: stateHero.path};
        if (solutionConfig.sandbox.thirdParty) {
          runtimeOverrides = stateHero;
        }
      }

      const baseHeroConfig = HERO_CONFIGS[type];
      // Precedence: Runtime set hero values, solution set hero values, default type values.
      newHero = Object.assign({}, baseHeroConfig, {
        // shortTitle / name is the only required label for a solution, so all other labels fallback to it
        // or are generated from it.
        shortTitle: name || baseHeroConfig.shortTitle,
        title: longName || name || baseHeroConfig.title
      }, runtimeOverrides);
    }

    // Find and set the favicon to the new hero icon
    document.querySelectorAll('#favicon').forEach(link => {
      let extension = newHero.icon.substring(newHero.icon.lastIndexOf('.') + 1);
      link.type = extension === 'ico' ? 'image/x-icon' : 'image/ico';
      link.href = newHero.icon;
    });

    return newHero;
  }

  getSandboxes = async () => {
    const {auth, profile, selectedImsOrg} = this.state;
    const localSandboxes = await unifiedShellSession.get('palmSandboxes', selectedImsOrg) || {};

    try {
      const selectedSandboxName = getSandboxFromPath(this.history.location.pathname) || localSandboxes.lastSelectedSandbox;
      // Asynchronously update local storage with the most recently fetched Sandboxes
      // On the next refresh, the sandboxes will be up to date.
      const sandboxPromise = new Promise((resolve, reject) => {
        auth.gqlUserSandboxes().then(({userSandboxes}) => {
          this.setSandboxStorage({sandboxes: userSandboxes});
          resolve(userSandboxes);
        }).catch(() => reject());
      });
      // Only fetch sandboxes from gql if they are not in local storage
      const sandboxes = localSandboxes.sandboxes || await sandboxPromise;
      let selectedSandbox = sandboxes.find(sb => sb.name === selectedSandboxName);
      // If there is no selected sandbox or the sandbox is no longer valid
      if (!selectedSandbox) {
        const defaultSandbox = sandboxes.find(sb => sb.isDefault);
        selectedSandbox = defaultSandbox || sandboxes[0];
      }
      this.setSandboxStorage({lastSelectedSandbox: selectedSandbox.name, sandboxes});
      this.setSandboxes({sandboxes, selected: selectedSandbox});
    } catch (error) {
      this.setSandboxes({sandboxes: [], selected: null});
      this.metrics.info(`User ${profile && profile.userId} doesn't have access to sandboxes in org ${selectedImsOrg}.`);
      // Remove null palmSandbox entry from localStorage
      unifiedShellSession.remove('palmSandboxes', selectedImsOrg);
    }
  }

  setSandboxes = newSandbox => {
    this.setState({sandbox: {...this.state.sandbox, ...newSandbox}});
    const {name} = newSandbox.selected || {};

    if (name !== getSandboxFromPath(this.history.location.pathname)) {
      updateSandboxInPath(name, this.hasTenantInUrl);
    }
  }

  setSandboxStorage = async updates => {
    const {selectedImsOrg} = this.state;
    unifiedShellSession.update('palmSandboxes', updates, selectedImsOrg);
  }

  onSandboxChange = async selected => {
    const {selectedImsOrg} = this.state;
    unifiedShellSession.update('palmSandboxes', {lastSelectedSandbox: selected.name}, selectedImsOrg);
    this.setSandboxes({selected});
  }

  setSolutionConfig = async solutionConfig => {
    const {
      hero,
      sandbox,
      selectedImsOrg,
      shellResponse,
      sidebar
    } = this.state;

    const sandboxEnabled = (solutionConfig.palmSandbox || {}).enabled;
    const heroConfig = this.getHeroConfig(shellResponse, solutionConfig, hero);
    // Because we don't clear sandboxes on solution change we must reset the sandboxes if the new
    // solution doesn't support them.
    const sandboxConfig = sandboxEnabled ?
      Object.assign(sandbox, {enabled: sandboxEnabled}) :
      {enabled: false, onChange: this.onSandboxChange, sandboxes: [], selected: null};

    let sideNavOpen = await this.getSideNavState();
    if ((solutionConfig.sideNav || {}).closeOnLoad) {
      sideNavOpen = false;
    }

    this.setState({
      favicon: hero.icon,
      helpCenter: {
        solution: solutionConfig.appId
      },
      hero: heroConfig,
      nps: this.getNps(solutionConfig.nps),
      releaseType: solutionConfig.releaseType,
      sandbox: sandboxConfig,
      sidebar: {...sidebar, config: solutionConfig.sideNav || null},
      sideNavEnabled: !!solutionConfig.sideNav,
      sideNavOpen,
      solutionConfig
    }, () => {
      // updatePath triggers onHistoryChange which relies on solution config values from the state.
      // This code has been moved to the callback of setState so we can ensure that the new solution config values
      // have been set in state before onHistoryChange is called.
      this.metrics.event('Shell.solutionConfig.set', {solutionConfig});
      this.hasTenantInUrl = !solutionConfig.hideTenant;
      const tenant = this.orgTenantMap[selectedImsOrg] || selectedImsOrg;
      // Check if the org is allowed to access the new solution.
      if (selectedImsOrg && !this.isOrgPermissionedOnSolution(selectedImsOrg, solutionConfig)) {
        updatePath({path: '/home', tenant}, true, true, false);
        this.metrics.event('Shell.notPermissioned', {appId: solutionConfig.appId, selectedImsOrg});
        return this.showToast('orgHomeRedirectToast', {timeout: TOAST_TIMEOUT, variant: 'info'});
      }
      // If the selected sandbox is defined, the previous solution and the new solution both support sandboxes
      // therefore we don't need to fetch sandboxes again. Only fetch if coming from a solution without sandboxes to a
      // solution with sandboxes enabled and the imsOrg is set (it is not a refresh or login).
      sandboxConfig.selected || selectedImsOrg && sandboxEnabled && this.getSandboxes();
      updatePath({tenant}, true, this.hasTenantInUrl, !!sandboxEnabled);
    });
  }

  getNps = newNps => {
    const forceNps = getQueryValue('forcenps');
    const nps = Object.assign({
      enabled: false,
      onSubmit: npsScore => this.setState({npsScore}),
      sampling: 10,
      showToAdobeUsers: true
    }, newNps || {});
    forceNps && Object.assign(nps, {force: forceNps === 'true'});
    return nps;
  }

  setSubOrgs = (orgToSubOrgsMap, selectedSubOrg) => {
    this.setState({orgToSubOrgsMap});
    if (!selectedSubOrg || !this.state.selectedImsOrg) {
      return;
    }

    for (let subOrg of orgToSubOrgsMap[this.state.selectedImsOrg] || []) {
      if (subOrg.id === selectedSubOrg.id) {
        this.setState({selectedSubOrg});
        return this.storeSubOrg(selectedSubOrg);
      }
    }

    throw Error('selectedSubOrg is not a child of the current ims org');
  }

  updateTargetNode = () => {
    const {coachMark} = this.state;
    if (!coachMark.enabled) {
      return;
    }
    const targetNode = document.querySelector(coachMark.element);
    if (!targetNode) {
      this.setState({coachMark: {...coachMark, hidden: true}});
      return;
    }
    // Get the position of the leftmost edge of the element and the width of the element
    const {left, width} = targetNode.getBoundingClientRect();
    // Calculation for the middle of the element minus indicator width
    const coachMarkPosition = left + (width / 2) - 16;
    this.setState({coachMark: {...coachMark, hidden: false}, coachMarkPosition});
    return coachMarkPosition;
  }

  /**
   * Read/Upsert/Delete settings.
   * @param {Object} data Required: JSONObject contaning requested groupId,
   * settings, and corresponding settings level(optional).
   * GET ex- {groupId: 'group1', level: SETTINGS_LEVEL.USER,
   * settings: {key1: default value to fallback}}.
   * POST ex- {groupId: 'group1', settings: {key1: value1}}.
   * @param {string} method Required: Method Type: GET/POST/DELETE.
   * @param {Object} isUnifiedShell Optional: To be set to true
   * by unified shell apps to consume Unified Shell Application space.
   * @param {Object} appId - Optional: Used by unifiedShell apps to
   * consume settings from different app space overriding unified shell app space.
   * ex- settingsHandler(data, 'GET', false, 'not-unifiedshell-appId')
   *
   * @returns {Promise} - Settings Response.
   */
  async settingsHandler(data, method, isUnifiedShell, appId) {
    const {auth, solutionConfig, selectedImsOrg, sandbox} = this.state;
    const environment = this.config.serviceEnvironment;
    let accessToken = auth.fetchAccessToken();
    const clientId = auth.getClientId();
    const selectedAppId = appId || isUnifiedShell && UNIFIED_SHELL_APPID
    // pick solutionConfig.settingsAppId if present rather than solutionConfig.appId
    || solutionConfig.settingsAppId || solutionConfig.appId;
    const settingsPackageLoadTimer = this.metrics.start('SettingsLoadJS');
    const SettingsService = await getSettingsService();
    settingsPackageLoadTimer.time('done', `Core: Settings Request for appId: ${selectedAppId} and orgId: ${selectedImsOrg}`);
    SettingsService.configure({
      env: environment,
      imsInfo: {accessToken, clientId}
    });
    let selectedSandbox = undefined;
    if (sandbox && sandbox.selected && sandbox.selected.type !== SANDBOX_TYPE_PRODUCTION) {
      selectedSandbox = sandbox.selected.name;
    }
    return await SettingsService.settingsHandler({
      appId: selectedAppId,
      data,
      imsOrgId: selectedImsOrg,
      method,
      sandbox: selectedSandbox
    });
  }

  /**
   * Gets token from IMS.
   * @param {string} clientId Client id string.
   * @param {string} scope List of scopes.
   * @returns {Object} Profile, token.
   */
  async getTokenFromIMS(clientId, scope) {
    // This should always pick up the instance as opposed to creating a new
    // instance. If this changes, will need to pass configuration into this.
    const auth = this.state.auth || await AuthInstance();

    try {
      const customTokenTimer = this.metrics.start('CustomToken', {clientId});
      const res = await auth.fetchProfileAndAccessTokenInfo(clientId, scope);
      customTokenTimer.time('done', {clientId, success: (res && res.access_token)});
      const {access_token, ...profile} = res;
      return {profile, token: access_token};
    } catch (e) {
      this.metrics.error(`Failed to fetch token for client id: ${clientId}`, e);
      return null;
    }
  }

  /**
   * Gets token for re-auth from cache or IMS.
   * @param {string} client_id Client id string.
   * @param {string} scope List of scopes.
   * @returns {Object} Profile, token.
   */
  async getToken({client_id, scope}) {
    const cachedInfo = await this.getCachedToken(client_id, scope);

    // If the cached token is available, return it.
    if (cachedInfo) {
      return cachedInfo;
    }

    // Prevent duplicate calls by storing promises.
    const key = btoa(client_id + scope);
    this.tokenPromises = this.tokenPromises || {};
    let authPromise = this.tokenPromises[key];

    // If there is an existing promise inflight, wait and return it.
    if (authPromise) {
      return await authPromise;
    }

    // Create a promise and store it to prevent redundant calls.
    this.tokenPromises[key] = authPromise = this.getTokenFromIMS(client_id, scope);

    // Need to asynchronously fetch the token from IMS for the custom client id.
    // Upon completion, sends the payload if one is queued.
    const newIms = await authPromise;
    newIms && this.updateCachedTokens(client_id, scope, newIms);

    delete this.tokenPromises[key];
    return newIms;
  }

  /**
   * Gets cached token for sandbox re-auth. Should retrieve from cache if
   * switching solutions within 10 minutes.
   * @param {string} clientId Client id string.
   * @param {string} scope List of scopes.
   * @returns {Object} Profile, token, and date info.
   */
  getCachedToken = async (clientId, scope) => {
    let cachedTokens = await storage.session.get('shellCachedTokens') || {};
    let key = btoa(clientId + scope);

    if (!(key in cachedTokens)) {
      return;
    }

    let info = cachedTokens[key];
    let dateDiff = Date.now() - info.dateStored;
    if (dateDiff < TEN_HOURS) {
      this.updateCachedTokens(clientId, scope, info);
      return info;
    }
    // clear expired token by passing no token
    this.updateCachedTokens(clientId, scope);
  }

  /**
   * Sets and updates cached token for sandbox re-auth. Stores token with
   * current date using clientId and scopes.
   * @param {string} clientId Client id string.
   * @param {string} scope List of scopes.
   * @param {Object} ims Ims object with profile and token info.
   */
  updateCachedTokens = async (clientId, scope, ims) => {
    let cachedTokens = await storage.session.get('shellCachedTokens') || {};
    let key = btoa(clientId + scope);
    if (!ims) {
      delete cachedTokens[key];
    } else {
      cachedTokens[key] = {
        dateStored: Date.now(),
        profile: ims.profile,
        token: ims.token
      };
    }
    storage.session.set('shellCachedTokens', cachedTokens, TEN_HOURS);
  }

  async loadCaas(callback) {
    const CaasFrame = await getCaasFrame();
    this.setState({caas: (<CaasFrame onReady={callback}></CaasFrame>)});
  }

  renderNoAccess() {
    const {
      defaultComponent,
      hasAccountClusterData,
      locale,
      profile,
      userAccounts
    } = this.state;
    if (!hasAccountClusterData) {
      return defaultComponent;
    }

    const account = userAccounts.find(({userId, userType}) => userId === profile.userId && userType !== AUTH_TYPE.TYPE2E);
    const hasOtherAccounts = account && userAccounts.length > 1;

    return (
      <React.Suspense fallback={defaultComponent}>
        <ShellNoAccess hasOtherAccounts={!!hasOtherAccounts} locale={locale} />
      </React.Suspense>
    );
  }

  render() {
    const {
      appContainer,
      accessToken,
      basePath,
      caas,
      coachMark,
      customEnvLabel,
      customHeroClick,
      customButtons,
      customSearch,
      defaultComponent,
      digitalData,
      enhancedImsOrgs,
      environment,
      error,
      feedback,
      hasAccountClusterData,
      helpCenter,
      hero,
      imsOrgs,
      locale,
      modal,
      nps,
      onCustomButtonClick,
      orgToSubOrgsMap,
      profile,
      releaseType,
      sandbox,
      selectedImsOrg,
      selectedSubOrg,
      settingsResponse,
      shellResponse,
      showNoAccessShell,
      sidebar,
      sideNavEnabled,
      sideNavOpen,
      solutionConfig,
      solutionLoaded,
      supportsViewportTakeover,
      timelineResponse,
      userAvatar,
      userOrgPreferences,
      viewportTakeover,
      workspaces
    } = this.state;

    let content = this.props.children;
    showNoAccessShell && (content = this.renderNoAccess());
    error && (content = <ErrorView type={error} />);

    const allOrgs = enhancedImsOrgs.length ? enhancedImsOrgs : imsOrgs || [];
    // This does the equivalent of a lodash groupBy(customButtons, 'scope');
    const scopedCustomButtons = (customButtons || []).reduce((r, v, i, a, k = v['scope']) => ((r[k] || (r[k] = [])).push(v), r), {});

    const globalSearchConfig = {
      accessToken,
      enabled: (solutionConfig.unifiedSearch || {}).enabled && accessToken && (this.state.featureFlags || {})['unified-search'] === 'true',
      selectedImsOrg
    };

    return (
      <CoreContext.Provider value={this.getComponentContext()}>
        <ShellWrapper
          accessToken={accessToken}
          appContainer={appContainer}
          appId={solutionConfig.appId}
          basePath={basePath}
          clientId={this.config.ims.client_id}
          coachMark={coachMark}
          customButtons={scopedCustomButtons}
          customEnvLabel={customEnvLabel}
          customHeroClick={customHeroClick}
          customSearch={customSearch}
          defaultComponent={defaultComponent}
          digitalData={digitalData}
          environment={environment}
          feedback={feedback}
          globalSearch={globalSearchConfig}
          hasAccountClusterData={hasAccountClusterData}
          helpCenter={helpCenter}
          hero={hero}
          imsOrgs={allOrgs}
          locale={locale}
          modal={modal}
          noAccess={showNoAccessShell}
          noSolution={!solutionConfig || !solutionConfig.path}
          nps={nps}
          onCustomButtonClick={onCustomButtonClick}
          onImsOrgChange={this.updateUrlOnOrgChange}
          onSignOut={this.onSignOut}
          orgToSubOrgsMap={orgToSubOrgsMap}
          profile={profile}
          pulse={{
            accessToken,
            enabled: !!timelineResponse && !!accessToken && Object.keys(shellResponse).length > 0,
            environment: this.config.serviceEnvironment,
            locale,
            profile,
            selectedImsOrg,
            settingsResponse,
            settingsService: this.settingsRef,
            shellResponse,
            timelineResponse,
            userOrgPreferences
          }}
          releaseType={releaseType}
          sandbox={sandbox}
          selectedImsOrg={selectedImsOrg}
          selectedSubOrg={selectedSubOrg}
          selectedTenantId={cleanTenant(this.orgTenantMap[selectedImsOrg])}
          serviceEnvironment={this.config.serviceEnvironment}
          settingsService={this.settingsRef}
          shellResponse={shellResponse}
          sideNavConfig={sidebar.config}
          sideNavEnabled={sideNavEnabled && sidebar.visible !== false}
          sideNavOpen={sideNavOpen}
          solutionLoaded={solutionLoaded}
          supportsViewportTakeover={supportsViewportTakeover}
          toggleSideNav={this.toggleSideNav}
          translationService={this.translationService}
          userAvatar={userAvatar}
          viewportTakeover={viewportTakeover}
          workspaces={workspaces}
        >
          <React.Fragment>
            {caas}
            {content}
          </React.Fragment>
        </ShellWrapper>
      </CoreContext.Provider>
    );
  }
}

Core.propTypes = {
  config: PropTypes.object,
  isLogin: PropTypes.bool,
  metrics: PropTypes.object,
  org: PropTypes.string
};

export default withLocalization(Core, {displayName: 'Core'});
